/**
 * Client-side port of the server's duplicate-folding helpers, used by the
 * live duplicate-check at contact creation time. Keep these in sync with
 * apps/api/src/import/duplicate-rules.ts (foldName / normalizeDate /
 * normalizeEmail) so the Anlage-Warnung and the later cluster detection agree.
 */

/** Academic / professional title tokens dropped during name folding. */
const NAME_TITLE_TOKENS = new Set([
  "dr",
  "prof",
  "dipl",
  "ing",
  "med",
  "mag",
  "phd",
  "msc",
  "bsc",
  "llm",
  "rer",
  "nat",
  "habil",
  "jur",
  "oec",
]);

/**
 * Fold a personal/company name into a comparison key: lowercase, umlauts
 * expanded (ä→ae, ö→oe, ü→ue, ß→ss), title prefixes + punctuation removed,
 * whitespace collapsed. Mirrors the server's foldName exactly.
 */
export const foldName = (value: unknown): string => {
  const lowered = String(value ?? "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
  const tokens = lowered
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !NAME_TITLE_TOKENS.has(t));
  return tokens.join(" ").trim();
};

/** Normalize a date to its leading YYYY-MM-DD slice (or '' for empty). */
export const normalizeDate = (value: unknown): string => {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const raw = String(value).trim();
  if (!raw) return "";
  return raw.slice(0, 10);
};

/** Normalize an email: lowercase, trim, strip mailto:/surrounding brackets. */
export const normalizeEmail = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/^mailto:/, "")
    .replace(/^<|>$/g, "")
    .trim();

export interface DuplicateCheckCandidate {
  id: number;
  lastname?: string;
  firstName?: string;
  displayName?: string;
  birthDate?: string;
  type?: string | null;
  contactNbrInt?: string | null;
  defaultEmail?: string | null;
  city?: string | null;
}

export interface DuplicateCheckInput {
  isCompany: boolean;
  firstName: string;
  lastName: string;
  birthDate: string;
  email: string;
}

export interface RankedCandidate {
  candidate: DuplicateCheckCandidate;
  score: number;
  reasons: string[];
}

/**
 * Rank existing contacts as possible duplicates of the being-created contact.
 *
 * Name similarity is the FIRST gate (per the dedup-clustering strategy):
 *  - Firma  → folded company name (lastname/displayName) must match.
 *  - Person → folded lastname must match; matching firstname adds weight.
 * Birthdate / e-mail only add weight as tiebreakers AFTER the name gate, never
 * on their own — a private contact often inherits the company e-mail, so e-mail
 * alone must never cluster Firma↔Person.
 */
export function rankCandidates(
  input: DuplicateCheckInput,
  candidates: DuplicateCheckCandidate[],
): RankedCandidate[] {
  const inLast = foldName(input.lastName);
  const inFirst = foldName(input.firstName);
  const inDate = normalizeDate(input.birthDate);
  const inEmail = normalizeEmail(input.email);

  const ranked: RankedCandidate[] = [];
  for (const c of candidates) {
    const reasons: string[] = [];
    let score = 0;

    if (input.isCompany) {
      const candCompany = foldName(c.displayName ?? c.lastname);
      if (!inLast || candCompany !== inLast) continue; // name gate
      score += 40;
      reasons.push("Firmenname");
    } else {
      const candLast = foldName(c.lastname ?? c.displayName);
      if (!inLast || candLast !== inLast) continue; // name gate
      score += 40;
      reasons.push("Nachname");
      const candFirst = foldName(c.firstName);
      if (inFirst && candFirst === inFirst) {
        score += 25;
        reasons.push("Vorname");
      }
    }

    const candDate = normalizeDate(c.birthDate);
    if (inDate && candDate && inDate === candDate) {
      score += 20;
      reasons.push("Geburtsdatum");
    }
    const candEmail = normalizeEmail(c.defaultEmail);
    if (inEmail && candEmail && inEmail === candEmail) {
      score += 15;
      reasons.push("E-Mail");
    }

    ranked.push({ candidate: c, score, reasons });
  }

  ranked.sort(
    (a, b) =>
      b.score - a.score ||
      (a.candidate.lastname ?? "").localeCompare(b.candidate.lastname ?? ""),
  );
  return ranked.slice(0, 5);
}
